/**
 * useEnginePlayActions - 主业务层 (Facade Pattern)
 *
 * 职责：
 * 1. 组合各子 Hook (Session, MessageSync, Polling, Galgame)
 * 2. 暴露统一的 API 给组件使用
 * 3. 协调各模块间的交互
 */
import { useCallback, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import { logger } from '@/lib/logger'
import { showErrorData } from '@/lib/toast'
import { useTransactions } from '@/features/transactions'
import { type ServerCurrentScene } from '../lib/adapters'
import { useEngineStore } from '../stores/use-engine-store'
import { SenderRole } from '../types/chat-model'
import type { MakeChoiceVariables } from '../types/mutate'
import { useChatSession } from './use-chat-session'
import { useEnginePlay } from './use-engine-play'
import { useGalgameController } from './use-galgame-controller'
import { clearMessagesCache } from './use-infinite-messages'
import { useMessageSync } from './use-message-sync'
import { usePollingHandler } from './use-polling-handler'

interface UseEnginePlayActionsOptions {
  /**
   * 是否跳过初始的数据获取
   */
  skipInitialFetch?: boolean
}

export const useEnginePlayActions = (
  sessionId?: string,
  options: UseEnginePlayActionsOptions = {}
) => {
  const { skipInitialFetch = false } = options
  const queryClient = useQueryClient()

  // ========== 1. 基础依赖 (Store & Transactions) ==========
  const { setMessages, setHasMore, setLoadingMore } = useEngineStore(
    useShallow((s) => ({
      setMessages: s.messages.setMessages,
      setHasMore: s.messages.setHasMore,
      setLoadingMore: s.messages.setLoadingMore,
    }))
  )
  const isPolling = useEngineStore((s) => s.choice.isPolling)
  const { invalidateTransactions } = useTransactions()

  // ========== 2. 组合子 Hook ==========

  // 2.1 核心数据与 API (Mutation States & Query Data)
  const {
    messages: serverMessages,
    hasMore,
    isMessagesLoading,
    isLoadingMore,
    isRegenerating,
    isDeletingMessage,
    fetchNextPage,
    makeChoiceAsync,
    regenerateAsync,
    deleteMessageAsync,
    refetchMemory,
    refreshAllData,
  } = useEnginePlay(sessionId, { skipInitialFetch })

  // 2.2 消息同步
  const { syncMessagesToStore, syncAppendSceneToStore, appendUserMessage } =
    useMessageSync(sessionId)

  // 2.3 会话管理
  const session = useChatSession(sessionId, { skipInitialFetch })

  // 2.4 Galgame 控制器
  const galgame = useGalgameController()

  // 2.5 轮询处理
  usePollingHandler({
    sessionId,
    onResult: syncAppendSceneToStore,
    onTransactionInvalidate: invalidateTransactions,
  })

  // ========== 3. 同步分页状态 ==========
  useEffect(() => {
    setHasMore(hasMore)
  }, [hasMore, setHasMore])

  useEffect(() => {
    setLoadingMore(isLoadingMore)
  }, [isLoadingMore, setLoadingMore])

  // ========== 4. 业务方法封装 ==========

  /**
   * 发送选择
   */
  const sendChoice = useCallback(
    async (variables: MakeChoiceVariables, displayText?: string) => {
      try {
        if (displayText) {
          appendUserMessage(displayText)
        }

        const newScene = await makeChoiceAsync(variables)

        if (newScene && sessionId) {
          syncAppendSceneToStore(newScene as ServerCurrentScene, sessionId)
          invalidateTransactions()
        }
        return newScene
      } catch (e) {
        logger.error('发送选择失败', e)
        return null
      }
    },
    [
      makeChoiceAsync,
      appendUserMessage,
      syncAppendSceneToStore,
      sessionId,
      invalidateTransactions,
    ]
  )

  /**
   * 发送自定义文本
   */
  const sendText = useCallback(
    async (text: string) => {
      if (!sessionId) return null
      return sendChoice(
        {
          sessionId,
          data: {
            choiceId: 'default_start_adventure',
            customText: text,
          },
        },
        text
      )
    },
    [sendChoice, sessionId]
  )

  /**
   * 删除最新对话
   */
  const deleteLatestMessage = useCallback(async () => {
    const currentMessages = useEngineStore.getState().messages.messages || []
    if (currentMessages.length === 0) return

    const lastMessage = currentMessages[currentMessages.length - 1]
    let deleteCount = 1

    if (lastMessage.role === SenderRole.AUTHOR) {
      const prevMessage = currentMessages[currentMessages.length - 2]
      if (prevMessage && prevMessage.role === SenderRole.USER) {
        deleteCount = 2
      }
    }

    const updatedMessages = currentMessages.slice(0, -deleteCount)
    setMessages(updatedMessages)
    galgame.initSegments(updatedMessages.length - 1)

    if (lastMessage.role === SenderRole.AUTHOR && sessionId) {
      try {
        await deleteMessageAsync({ sessionId, msgId: lastMessage.id })
      } catch (e) {
        logger.error('后端删除消息失败', e)
      }
    }
    // 清除消息缓存
    if (sessionId) {
      clearMessagesCache(queryClient, sessionId)
    }
  }, [setMessages, galgame, deleteMessageAsync, sessionId, queryClient])

  /**
   * 重新生成 AI 回复
   */
  const regenerateLatestMessage = useCallback(async () => {
    if (!sessionId) return

    const currentMessages = useEngineStore.getState().messages.messages || []
    if (currentMessages.length === 0) return

    const lastMessage = currentMessages[currentMessages.length - 1]

    // 记录乐观移除前的消息，失败时回滚，避免重新生成失败导致消息凭空消失（Bug-10）
    const snapshotForRollback =
      lastMessage.role === SenderRole.AUTHOR ? currentMessages : null

    if (lastMessage.role === SenderRole.AUTHOR) {
      const { mode, galgameState } = useEngineStore.getState()
      if (
        mode.mode === 'galgame' &&
        galgameState.currentMessageIndex === currentMessages.length - 1
      ) {
        if (currentMessages.length > 1) {
          galgame.initSegments(currentMessages.length - 2)
        }
      }
      const updatedMessages = currentMessages.slice(0, -1)
      setMessages(updatedMessages)
    }

    try {
      await regenerateAsync({ sessionId })
    } catch (e) {
      if (snapshotForRollback) {
        setMessages(snapshotForRollback) // 回滚乐观移除
      }
      logger.error('重新生成失败', e)
      showErrorData('重新生成失败', e)
    }
  }, [sessionId, setMessages, regenerateAsync, galgame])

  // ========== 5. 返回统一 API ==========
  return useMemo(
    () => ({
      // 状态
      isPolling,
      isMessagesLoading,
      isSessionLoading: !serverMessages && !skipInitialFetch,

      // 会话生命周期 (from useChatSession, includes sessionInfo, serverMessages)
      ...session,

      // 发送相关
      sendChoice,
      sendText,

      // 消息操作
      deleteLatestMessage,
      regenerateLatestMessage,

      // Galgame 控制 (from useGalgameController)
      ...galgame,

      // 辅助函数 (from useMessageSync)
      syncMessagesToStore,
      syncAppendSceneToStore,
      refreshMemory: refetchMemory,
      refreshAllData,

      // 加载状态 (from useEnginePlay)
      isRegenerating,
      isDeletingMessage,
      hasMore,
      fetchNextPage,
    }),
    [
      isPolling,
      isMessagesLoading,
      serverMessages,
      skipInitialFetch,
      session,
      sendChoice,
      sendText,
      deleteLatestMessage,
      regenerateLatestMessage,
      galgame,
      syncMessagesToStore,
      syncAppendSceneToStore,
      refetchMemory,
      refreshAllData,
      isRegenerating,
      isDeletingMessage,
      hasMore,
      fetchNextPage,
    ]
  )
}
